import { useTransform, type MotionValue } from "framer-motion";
import {
  OPACITY_FALLOFF_PITCHES,
  OPACITY_MIN,
  ROTATE_X_MAX_DEG,
  ROTATE_Y_MAX_DEG,
  SCALE_FALLOFF_PITCHES,
  SCALE_MIN,
} from "./constants";

interface UseCarouselCardMotionParams {
  doubledIndex: number;
  pitch: number;
  scrollX: MotionValue<number>;
}

interface CarouselCardMotion {
  compensateX: MotionValue<number>;
  cylinderZ: MotionValue<number>;
  opacity: MotionValue<number>;
  rotateX: MotionValue<number>;
  rotateY: MotionValue<number>;
  scale: MotionValue<number>;
}

function getCompensatedX(screenX: number, rotateY: number): number {
  return screenX * (Math.cos((rotateY * Math.PI) / 180) - 1);
}

function getCylinderZ(screenX: number, rotateY: number): number {
  return -screenX * Math.sin((rotateY * Math.PI) / 180);
}

export function useCarouselCardMotion({
  doubledIndex,
  pitch,
  scrollX,
}: UseCarouselCardMotionParams): CarouselCardMotion {
  const homeScroll = doubledIndex * pitch;
  const screenX = useTransform(scrollX, (current) => homeScroll - current);
  const distance = useTransform(screenX, (value) => Math.abs(value));
  const scale = useTransform(
    distance,
    [0, pitch * SCALE_FALLOFF_PITCHES],
    [1, SCALE_MIN],
    { clamp: true },
  );
  const opacity = useTransform(
    distance,
    [0, pitch * OPACITY_FALLOFF_PITCHES],
    [1, OPACITY_MIN],
    { clamp: true },
  );
  const rotateY = useTransform(
    screenX,
    [-pitch, 0, pitch],
    [-ROTATE_Y_MAX_DEG, 0, ROTATE_Y_MAX_DEG],
    { clamp: true },
  );
  const rotateX = useTransform(
    screenX,
    [-pitch, 0, pitch],
    [-ROTATE_X_MAX_DEG, 0, ROTATE_X_MAX_DEG],
    { clamp: true },
  );
  const compensateX = useTransform(
    [screenX, rotateY] as const,
    ([screen, rotation]: number[]) => getCompensatedX(screen, rotation),
  );
  const cylinderZ = useTransform(
    [screenX, rotateY] as const,
    ([screen, rotation]: number[]) => getCylinderZ(screen, rotation),
  );

  return {
    compensateX,
    cylinderZ,
    opacity,
    rotateX,
    rotateY,
    scale,
  };
}
